import Highcharts from 'highcharts';
import PptxGenJS from 'pptxgenjs';
import { svgToBase64Png } from '../Forecast/exportForecastToPowerPoint';

// The hedge position bar: drawn over its cell on a slide, and written out as the cell's text when copied.
export interface HedgePlanExportBar {
  minimumPercent: number;
  maximumPercent: number;
  currentPercent: number;
  minimumLabel: string;
  maximumLabel: string;
  currentLabel: string;
  // Shown in the warning colour when the season has a recommendation.
  recommended: boolean;
}

export interface HedgePlanExportCell {
  // Lines are separated by '\n'.
  text: string;
  colspan?: number;
  rowspan?: number;
  fill?: string;
  color?: string;
  bold?: boolean;
  bar?: HedgePlanExportBar;
}

// A season's chart and matrix, laid out like the Forecast export: the chart as a picture, the matrix as a table.
export interface HedgePlanChartSlide {
  // Matches the season's row in the table, which the slide repeats above the chart.
  periodKey: string;
  title: string;
  chartOptions?: Highcharts.Options;
  matrixRows: HedgePlanExportCell[][];
}

export interface HedgePlanExportTable {
  title: string;
  subtitle: string;
  headerRows: HedgePlanExportCell[][];
  bodyRows: HedgePlanExportCell[][];
  // The season each body row belongs to.
  bodyRowKeys: string[];
  columnWidths: number[];
}

const ROWS_PER_SLIDE = 6;
const SLIDE_TABLE_WIDTH = 12.5;
const TABLE_X = 0.4;
const TABLE_Y = 1.2;
const HEADER_ROW_HEIGHT = 0.55;
const BODY_ROW_HEIGHT = 0.8;
// The export keeps its own light palette, so slides look the same whichever theme the screen is in.
const BORDER_COLOR = 'D9DDE3';
const WHITE = 'FFFFFF';
const STRIPE = 'F6F7F9';
const DARK_TEXT = '1F2933';
const MUTED_TEXT = '6B7280';
const BAR_TRACK = 'E5E7EB';
const BAR_FILL = '2E6DA4';
const BAR_RECOMMENDED_FILL = 'C62828';
const BAR_TICK = '4B5563';

// Theme colours come back as hex or rgb(); PowerPoint wants six hex digits without the '#'.
function toHex(color: string | undefined, fallback: string): string {
  if (!color) {
    return fallback;
  }
  const rgb = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgb) {
    return rgb.slice(1, 4).map((part) => Number(part).toString(16).padStart(2, '0')).join('').toUpperCase();
  }
  const hex = color.replace('#', '').trim();
  return hex.length === 3 ? hex.split('').map((digit) => digit + digit).join('').toUpperCase() : hex.toUpperCase() || fallback;
}

function toSlideCell(cell: HedgePlanExportCell, isHeader: boolean, isStriped = false) {
  return {
    text: cell.bar ? '' : cell.text,
    options: {
      colspan: cell.colspan,
      rowspan: cell.rowspan,
      bold: cell.bold ?? isHeader,
      color: toHex(cell.color, isHeader ? WHITE : DARK_TEXT),
      fill: { color: toHex(cell.fill, isHeader ? '0B1F3A' : isStriped ? STRIPE : WHITE) },
      align: 'center' as const,
      valign: 'middle' as const,
    },
  };
}

// A slim bar across the middle of the cell: the fill up to the current hedge, ticks at the minimum and
// maximum with their labels above and below, and the hedged amount just past the fill.
function drawBar(slide: PptxGenJS.Slide, presentation: PptxGenJS, bar: HedgePlanExportBar, x: number, y: number, w: number, h: number) {
  const clamp = (percent: number) => Math.min(100, Math.max(0, percent));
  const padding = 0.12;
  const left = x + padding;
  const width = w - padding * 2;
  const barHeight = 0.16;
  const barTop = y + h / 2 - barHeight / 2;
  const at = (percent: number) => left + (width * clamp(percent)) / 100;
  const current = clamp(bar.currentPercent);

  slide.addShape(presentation.ShapeType.rect, { x: left, y: barTop, w: width, h: barHeight, fill: { color: BAR_TRACK }, line: { type: 'none' } });
  if (current > 0) {
    slide.addShape(presentation.ShapeType.rect, { x: left, y: barTop, w: (width * current) / 100, h: barHeight, fill: { color: bar.recommended ? BAR_RECOMMENDED_FILL : BAR_FILL }, line: { type: 'none' } });
  }

  const tick = (percent: number) => slide.addShape(presentation.ShapeType.line, { x: at(percent), y: barTop - 0.05, w: 0, h: barHeight + 0.1, line: { color: BAR_TICK, width: 1 } });
  tick(bar.minimumPercent);
  tick(bar.maximumPercent);

  // A label sits centred on its tick, except near either end, where it lines up with the edge instead.
  const label = (text: string, percent: number, top: number) => {
    const labelWidth = Math.min(width, 0.1 + text.length * 0.05);
    const position = clamp(percent);
    const labelLeft = position <= 12 ? at(position) : position >= 88 ? at(position) - labelWidth : at(position) - labelWidth / 2;
    slide.addText(text, {
      x: Math.min(Math.max(labelLeft, left), left + width - labelWidth), y: top, w: labelWidth, h: 0.15,
      fontSize: 7, color: MUTED_TEXT, align: position <= 12 ? 'left' : position >= 88 ? 'right' : 'center', valign: 'middle', margin: 0,
    });
  };
  label(`Min ${bar.minimumLabel}`, bar.minimumPercent, barTop - 0.2);
  label(`Max ${bar.maximumLabel}`, bar.maximumPercent, barTop + barHeight + 0.05);

  // Past the fill, or inside it once the fill is too close to the right edge to fit the amount after it.
  const insideFill = current > 78;
  const fillEnd = at(current);
  // Starts after any tick sitting just past the fill, so the amount doesn't land on it.
  const labelStart = [bar.minimumPercent, bar.maximumPercent]
    .map(clamp)
    .filter((percent) => percent >= current && percent - current < 12)
    .reduce((furthest, percent) => Math.max(furthest, at(percent)), fillEnd);
  slide.addText(bar.currentLabel, {
    x: insideFill ? left : labelStart + 0.04,
    y: barTop - 0.04,
    w: insideFill ? Math.max(0.3, fillEnd - left - 0.04) : Math.max(0.3, left + width - labelStart - 0.04),
    h: barHeight + 0.08,
    fontSize: 9, bold: true, align: insideFill ? 'right' : 'left', valign: 'middle', margin: 0,
    color: insideFill ? WHITE : DARK_TEXT,
  });
}

// Draws the chart off screen at the Forecast export's size, takes its picture, and removes it again.
async function chartImage(options: Highcharts.Options, width: number, height: number): Promise<string | undefined> {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-10000px';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  document.body.appendChild(container);
  try {
    const chart = Highcharts.chart(container, { ...options, chart: { ...options.chart, width, height, animation: false } });
    const svgElement = chart.container.querySelector('svg');
    // The picture is drawn outside the page, where theme colours like var(--font-color) mean nothing,
    // so each one is swapped for the colour the page is using.
    const pageStyle = getComputedStyle(document.body);
    const svgMarkup = svgElement
      ? new XMLSerializer().serializeToString(svgElement).replace(/var\((--[\w-]+)\)/g, (match, name: string) => pageStyle.getPropertyValue(name).trim() || match)
      : '';
    chart.destroy();
    return svgMarkup ? await svgToBase64Png(svgMarkup, width, height) : undefined;
  } finally {
    container.remove();
  }
}

interface TableLayout {
  y: number;
  headerRowHeight: number;
  bodyRowHeight: number;
  fontSize: number;
  // Index of the first row on the slide in the whole table, so the striping carries across slides.
  firstRowIndex: number;
}

// Rows are fixed heights, so each hedge position bar can be placed exactly over its cell. Returns the table's bottom.
function addHedgesTable(slide: PptxGenJS.Slide, presentation: PptxGenJS, table: HedgePlanExportTable, rows: HedgePlanExportCell[][], layout: TableLayout): number {
  const totalWidth = table.columnWidths.reduce((sum, width) => sum + width, 0);
  const columnWidths = table.columnWidths.map((width) => (width / totalWidth) * SLIDE_TABLE_WIDTH);
  const columnStarts = columnWidths.map((_, index) => TABLE_X + columnWidths.slice(0, index).reduce((sum, width) => sum + width, 0));
  const bodyTop = layout.y + table.headerRows.length * layout.headerRowHeight;

  slide.addTable([
    ...table.headerRows.map((row) => row.map((cell) => toSlideCell(cell, true))),
    ...rows.map((row, rowIndex) => row.map((cell) => toSlideCell(cell, false, (layout.firstRowIndex + rowIndex) % 2 === 1))),
  ], {
    x: TABLE_X,
    y: layout.y,
    colW: columnWidths,
    rowH: [...table.headerRows.map(() => layout.headerRowHeight), ...rows.map(() => layout.bodyRowHeight)],
    fontSize: layout.fontSize,
    border: { type: 'solid', pt: 0.5, color: BORDER_COLOR },
    autoPage: false,
  });

  rows.forEach((row, rowIndex) => row.forEach((cell, columnIndex) => {
    if (cell.bar) {
      drawBar(slide, presentation, cell.bar, columnStarts[columnIndex], bodyTop + rowIndex * layout.bodyRowHeight, columnWidths[columnIndex], layout.bodyRowHeight);
    }
  }));

  return bodyTop + rows.length * layout.bodyRowHeight;
}

async function addChartSlide(presentation: PptxGenJS, table: HedgePlanExportTable, chartSlide: HedgePlanChartSlide) {
  const slide = presentation.addSlide();
  slide.addText(chartSlide.title, { x: TABLE_X, y: 0.25, w: SLIDE_TABLE_WIDTH, h: 0.5, fontSize: 22, bold: true });

  // The season's row of the table, smaller, above the chart and matrix.
  const rowIndex = table.bodyRowKeys.indexOf(chartSlide.periodKey);
  const tableBottom = rowIndex < 0
    ? 0.8
    : addHedgesTable(slide, presentation, table, [table.bodyRows[rowIndex]], { y: 0.85, headerRowHeight: 0.4, bodyRowHeight: 0.62, fontSize: 8, firstRowIndex: 0 });

  // A wide chart on the left and a narrow matrix on the right, both the same height.
  const top = tableBottom + 0.15;
  const height = 7.3 - top;
  const matrixWidth = 2.3;
  const gap = 0.25;
  const chartWidth = SLIDE_TABLE_WIDTH - matrixWidth - gap;

  // Drawn at the slide's shape (96 pixels an inch), so the picture isn't stretched.
  const image = chartSlide.chartOptions ? await chartImage(chartSlide.chartOptions, Math.round(chartWidth * 96), Math.round(height * 96)) : undefined;
  if (image) {
    slide.addImage({ data: image, x: TABLE_X, y: top, w: chartWidth, h: height });
  }

  if (chartSlide.matrixRows.length > 0) {
    const columnCount = chartSlide.matrixRows[0].length;
    const firstColumnWidth = 0.9;
    slide.addTable(chartSlide.matrixRows.map((row, rowIndex) => row.map((cell) => toSlideCell(cell, rowIndex === 0))), {
      x: TABLE_X + chartWidth + gap,
      y: top,
      colW: [firstColumnWidth, ...Array.from({ length: columnCount - 1 }, () => (matrixWidth - firstColumnWidth) / Math.max(1, columnCount - 1))],
      rowH: height / chartSlide.matrixRows.length,
      fontSize: 8,
      border: { type: 'solid', pt: 0.5, color: BORDER_COLOR },
      autoPage: false,
    });
  }
}

// A native PowerPoint table rather than a picture, so it can be edited after export.
export async function downloadHedgePlanPowerPoint(table: HedgePlanExportTable, chartSlides: HedgePlanChartSlide[] = []): Promise<void> {
  const presentation = new PptxGenJS();
  presentation.layout = 'LAYOUT_WIDE';

  for (let start = 0; start < Math.max(table.bodyRows.length, 1); start += ROWS_PER_SLIDE) {
    const slide = presentation.addSlide();
    slide.addText(table.title, { x: TABLE_X, y: 0.25, w: SLIDE_TABLE_WIDTH, h: 0.5, fontSize: 22, bold: true });
    slide.addText(table.subtitle, { x: TABLE_X, y: 0.75, w: SLIDE_TABLE_WIDTH, h: 0.35, fontSize: 12, color: '666666' });
    addHedgesTable(slide, presentation, table, table.bodyRows.slice(start, start + ROWS_PER_SLIDE), {
      y: TABLE_Y, headerRowHeight: HEADER_ROW_HEIGHT, bodyRowHeight: BODY_ROW_HEIGHT, fontSize: 9, firstRowIndex: start,
    });
  }

  for (const chartSlide of chartSlides) {
    await addChartSlide(presentation, table, chartSlide);
  }

  // A date like 10/15/2026 can't go in a file name.
  await presentation.writeFile({ fileName: `${table.title} ${table.subtitle}`.trim().replace(/[\\/:*?"<>|]/g, '-') + '.pptx' });
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function toHtmlCell(cell: HedgePlanExportCell, isHeader: boolean, isStriped = false): string {
  const tag = isHeader ? 'th' : 'td';
  const spans = `${cell.colspan ? ` colspan="${cell.colspan}"` : ''}${cell.rowspan ? ` rowspan="${cell.rowspan}"` : ''}`;
  const style = [
    `background:#${toHex(cell.fill, isHeader ? '0B1F3A' : isStriped ? STRIPE : WHITE)}`,
    `color:#${toHex(cell.color, isHeader ? WHITE : DARK_TEXT)}`,
    `font-weight:${(cell.bold ?? isHeader) ? 700 : 400}`,
    `border:1px solid #${BORDER_COLOR}`,
    'padding:4px 8px',
    'text-align:center',
    'vertical-align:middle',
  ].join(';');
  return `<${tag}${spans} style="${style}">${cell.text.split('\n').map(escapeHtml).join('<br>')}</${tag}>`;
}

// PowerPoint, Word and Excel paste an HTML table as an editable table with its colours.
export async function copyHedgePlanTable(table: HedgePlanExportTable): Promise<void> {
  const rows = [
    ...table.headerRows.map((row) => `<tr>${row.map((cell) => toHtmlCell(cell, true)).join('')}</tr>`),
    ...table.bodyRows.map((row, rowIndex) => `<tr>${row.map((cell) => toHtmlCell(cell, false, rowIndex % 2 === 1)).join('')}</tr>`),
  ];
  const html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:10pt">${rows.join('')}</table>`;
  const text = table.bodyRows.map((row) => row.map((cell) => cell.text.replace(/\n/g, ' ')).join('\t')).join('\n');

  const ClipboardItemConstructor = (window as any).ClipboardItem;
  if (ClipboardItemConstructor && navigator.clipboard?.write) {
    await navigator.clipboard.write([new ClipboardItemConstructor({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    })]);
    return;
  }
  await navigator.clipboard.writeText(text);
}
